import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { fmtNum } from "@/lib/format";
import { getErrorMessage } from "@/lib/errors";

const WRITE_ROLES = ["ORG_ADMIN", "STORES", "PROCUREMENT"];

export default function Weighbridge() {
  const { user } = useAuth();
  const canWrite = WRITE_ROLES.includes(user?.role);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/weighbridge-tickets");
      setTickets(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-6" data-testid="weighbridge-page">
      <PageHeader title="Weighbridge" description="Gross/tare tickets for incoming material trucks. Net weight = gross − tare, and must be positive." action={canWrite && <CreateTicketDialog onCreated={load} />} />

      <div className="rounded-xl border border-border bg-card">
        {!loading && tickets.length === 0 ? (
          <EmptyState title="No weighbridge tickets yet" testId="weighbridge-empty-state" />
        ) : (
          <Table>
            <TableHeader>
              <TableRow><TableHead>Ticket #</TableHead><TableHead>Date</TableHead><TableHead>Vehicle</TableHead><TableHead>Supplier</TableHead><TableHead className="text-right">Gross</TableHead><TableHead className="text-right">Tare</TableHead><TableHead className="text-right">Net</TableHead></TableRow>
            </TableHeader>
            <TableBody>
              {tickets.map((t) => (
                <TableRow key={t.id} data-testid={`weighbridge-row-${t.ticket_number}`}>
                  <TableCell className="font-mono text-xs text-foreground">{t.ticket_number}</TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">{t.ticket_date}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{t.vehicle_no}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{t.supplier || "—"}</TableCell>
                  <TableCell className="text-right font-mono text-xs text-foreground">{fmtNum(t.gross_weight, 3)}</TableCell>
                  <TableCell className="text-right font-mono text-xs text-muted-foreground">{fmtNum(t.tare_weight, 3)}</TableCell>
                  <TableCell className="text-right font-mono text-xs font-semibold text-foreground">{fmtNum(t.net_weight, 3)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}

function CreateTicketDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ ticket_number: "", ticket_date: "", vehicle_no: "", supplier: "", gross_weight: "", tare_weight: "" });
  const [saving, setSaving] = useState(false);
  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/weighbridge-tickets", form);
      toast.success(`Ticket ${form.ticket_number} logged`);
      setForm({ ticket_number: "", ticket_date: "", vehicle_no: "", supplier: "", gross_weight: "", tare_weight: "" });
      setOpen(false);
      onCreated?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="create-weighbridge-ticket-button"><Plus className="h-4 w-4" /> New Ticket</Button></DialogTrigger>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader><DialogTitle>New Weighbridge Ticket</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3" data-testid="create-weighbridge-form">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Ticket number</Label><Input required value={form.ticket_number} onChange={set("ticket_number")} className="mt-1.5" data-testid="weighbridge-ticket-number-input" /></div>
            <div><Label>Date</Label><Input required type="date" value={form.ticket_date} onChange={set("ticket_date")} className="mt-1.5" data-testid="weighbridge-date-input" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Vehicle no.</Label><Input required value={form.vehicle_no} onChange={set("vehicle_no")} className="mt-1.5" data-testid="weighbridge-vehicle-input" /></div>
            <div><Label>Supplier</Label><Input value={form.supplier} onChange={set("supplier")} className="mt-1.5" data-testid="weighbridge-supplier-input" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Gross weight</Label><Input required type="number" step="0.001" value={form.gross_weight} onChange={set("gross_weight")} className="mt-1.5" data-testid="weighbridge-gross-input" /></div>
            <div><Label>Tare weight</Label><Input required type="number" step="0.001" value={form.tare_weight} onChange={set("tare_weight")} className="mt-1.5" data-testid="weighbridge-tare-input" /></div>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={saving} data-testid="create-weighbridge-submit-button">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Save</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
